import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { getRoles } from '../utils/utils';
import ErrorMessage from '../components/shared/ErrorMessage';
import { PageLayout, Select, Button, EmptyState, Table, SearchInput } from "../components/ui";

// Description and display metadata for pages
const pageMeta = {
  dashboard: { title: "Main Dashboard", desc: "Global overview & stats" },

  // Master Category
  master: { title: "Master Menu Access", desc: "Controls visibility of the Master submenu" },
  customers: { title: "Customers", desc: "Manage client database" },
  branches: { title: "Branches", desc: "Regional office control" },
  centers: { title: "Centers", desc: "Collection center points" },
  staff: { title: "Staff", desc: "Employee records" },
  accounts: { title: "Accounts", desc: "Financial ledger accounts" },
  fixeddeposite: { title: "Fixed Deposit", desc: "Manage term deposits" },
  chitfund: { title: "Chit Fund", desc: "Chit schemes management" },
  balance_book: { title: "Balance Book", desc: "Daily cash balance record" },
  businessLoan: { title: "Business Loan", desc: "Loan applications & approvals" },
  memberdetails: { title: "Member Details", desc: "Group membership details" },
  roles: { title: "Roles", desc: "Access level configuration" },
  funding_agencies: { title: "Funding Agencies", desc: "External fund sources" },
  permissions: { title: "Permissions", desc: "Page-level control matrix" },

  // Report Category
  report: { title: "Report Menu Access", desc: "Controls visibility of the Reports submenu" },
  dailyReport: { title: "Daily Report", desc: "Day-to-day operations summary" },
  dailyCollectionReport: { title: "Daily Collection", desc: "Cash collections log" },
  croCollectionsReport: { title: "CRO Collections", desc: "Officer-wise collection reports" },
  dayendprocess: { title: "Day End Process", desc: "Close daily accounts" },
  loandemand: { title: "Loan Demand", desc: "Expected vs actual collections" },
  future_demand_report: { title: "Future Demand", desc: "Upcoming payment projections" },
  overdue_report: { title: "Overdue Report", desc: "Non-performing assets listing" },
  par_report: { title: "PAR Report", desc: "Portfolio at risk analytics" },
  foreclosure_report: { title: "Foreclosure", desc: "Early loan closure records" },
  savings_report: { title: "Savings Report", desc: "Client savings balance report" },
  demand_collection_report: { title: "Demand Collection", desc: "Overall demand matching" },
  savings_follow_up_report: { title: "SFU Report", desc: "Savings follow-up tracking" },
  deposit_withdra_report: { title: "Deposit & Withdraw", desc: "Transaction history log" },
  security_deposit_report: { title: "Security Deposit", desc: "Collateral deposits tracking" },
  interest_report: { title: "Interest Report", desc: "Accrued interest summaries" },
  disbursedAmountReport: { title: "Disbursed Amount", desc: "Total loan disbursements" },
  field_officer_swap: { title: "Swapping Field Officer", desc: "Transfer customer portfolios" },
  master_data_report: { title: "Master Data Report", desc: "System master dump" },

  // Ledger Category
  ledger: { title: "Ledger Menu Access", desc: "Controls visibility of the Ledger submenu" },
  account_group: { title: "Add Accounts Group", desc: "Chart of accounts grouping" },
  ledger_group: { title: "Ledger Group", desc: "Financial ledger categories" },
  receipts: { title: "Receipts", desc: "Inward cash voucher entry" },
  payments: { title: "Payments", desc: "Outward cash voucher entry" },
  ledger_report: { title: "Ledger Report", desc: "Detailed statement of accounts" },
  balance_sheet: { title: "Balance Sheet", desc: "Assets & liabilities summary" }
};

// Section headers configuration
const sectionsConfig = {
  dashboard: {
    title: "Dashboard & Analytics",
    icon: (
      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
      </svg>
    )
  },
  master: {
    title: "Master Entities",
    icon: (
      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
      </svg>
    )
  },
  report: {
    title: "Report Pages",
    icon: (
      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M9 17v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-6-9V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3m-6 0h6" />
        <rect x="3" y="3" width="18" height="18" rx="2" />
      </svg>
    )
  },
  ledger: {
    title: "Ledger & Finance",
    icon: (
      <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    )
  }
};

// The exact order of pages as they appear in the Sidebar
const sidebarOrder = [
  "dashboard",

  // Master Section
  "master",
  "customers",
  "branches",
  "centers",
  "staff",
  "accounts",
  "fixeddeposite",
  "chitfund",
  "balance_book",
  "businessLoan",
  "memberdetails",
  "roles",
  "funding_agencies",
  "permissions",

  // Report Section
  "report",
  "dailyReport",
  "dailyCollectionReport",
  "croCollectionsReport",
  "dayendprocess",
  "loandemand",
  "future_demand_report",
  "overdue_report",
  "par_report",
  "foreclosure_report",
  "savings_report",
  "demand_collection_report",
  "savings_follow_up_report",
  "deposit_withdra_report",
  "security_deposit_report",
  "interest_report",
  "disbursedAmountReport",
  "field_officer_swap",
  "master_data_report",

  // Ledger Section
  "ledger",
  "account_group",
  "ledger_group",
  "receipts",
  "payments",
  "ledger_report",
  "balance_sheet"
];

const getSectionKey = (perm) => {
  if (perm.pageName === "dashboard") return "dashboard";
  if (perm.pageName === "master" || perm.parentPageName === "master") return "master";
  if (perm.pageName === "report" || perm.parentPageName === "report") return "report";
  if (perm.pageName === "ledger" || perm.parentPageName === "ledger") return "ledger";
  return "master"; // fallback
};

const Permissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchRoles = async () => {
      const roleOptions = await getRoles();
      setRoles(roleOptions);
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const getPermissionsData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/permissions?filter={"roleId":${selectedRoleId}}&includeDepth=1&relationshipType=childToParent`
        );

        const mappedPermissions = response.data.data.map((permission) => ({
          id: permission.id,
          roleId: permission.roleId,
          pageId: permission.pageId,
          pageName:
            permission.fk_permissions_belongsTo_pages_pageId?.name ||
            "Unknown",
          parentPageName:
            permission.fk_permissions_belongsTo_pages_pageId
              ?.parentPageName || "",
          view: permission.view,
          add: permission.add,
          edit: permission.edit,
          del: permission.del,
        }));

        // Sort permissions by the exact sidebarOrder
        mappedPermissions.sort((a, b) => {
          const indexA = sidebarOrder.indexOf(a.pageName);
          const indexB = sidebarOrder.indexOf(b.pageName);
          const valA = indexA === -1 ? 999 : indexA;
          const valB = indexB === -1 ? 999 : indexB;
          return valA - valB;
        });

        setPermissions(mappedPermissions);
        setOriginalPermissions(JSON.parse(JSON.stringify(mappedPermissions)));
        setErrorMsg("");
      } catch (error) {
        console.error(error);
        const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to fetch permissions";
        setErrorMsg(msg);
      }
    };

    if (selectedRoleId) {
      getPermissionsData();
    }
  }, [selectedRoleId]);

  const updateChildPermissions = (pageName, field) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm) => {
        if (perm.parentPageName === pageName) {
          return {
            ...perm,
            [field]: false,
            ...(field === "view" && { add: false, edit: false, del: false }),
          };
        }
        return perm;
      })
    );
  };

  const updateParentPermissions = (pageName, field) => {
    setPermissions((prevPermissions) =>
      prevPermissions.map((perm) => {
        if (perm.pageName === pageName) {
          return {
            ...perm,
            [field]: true,
            ...((field === "add" || field === "edit" || field === "del") && {
              view: true,
            }),
          };
        }
        return perm;
      })
    );
  };

  const handlePermissionChange = (pageId, field) => (event) => {
    const isChecked = event.target.checked;

    setPermissions((prevPermissions) =>
      prevPermissions.map((perm) => {
        if (perm.pageId === pageId) {
          if (!isChecked) {
            updateChildPermissions(perm.pageName, field);
            return {
              ...perm,
              [field]: false,
              ...(field === "view" && { add: false, edit: false, del: false }),
            };
          } else {
            updateParentPermissions(perm.parentPageName, field);
            return {
              ...perm,
              [field]: isChecked,
              ...((field === "add" || field === "edit" || field === "del") && {
                view: true,
              }),
            };
          }
        }
        return perm;
      })
    );
  };

  const savePermissions = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/updatePermissions/${selectedRoleId}`,
        { permissions, model: "permissions" }
      );
      if (response.data.message) {
        alert("Permissions updated successfully!");
        setOriginalPermissions(JSON.parse(JSON.stringify(permissions)));
        setErrorMsg("");
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || "Failed to save permissions";
      setErrorMsg(msg);
    }
  };

  const handleDiscard = () => {
    setPermissions(JSON.parse(JSON.stringify(originalPermissions)));
  };

  const isDirty = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);

  // Filter permissions based on search query
  const filteredPermissions = permissions.filter((perm) => {
    const meta = pageMeta[perm.pageName] || { title: perm.pageName, desc: "" };
    const query = searchQuery.toLowerCase();
    return (
      perm.pageName.toLowerCase().includes(query) ||
      meta.title.toLowerCase().includes(query) ||
      (meta.desc && meta.desc.toLowerCase().includes(query))
    );
  });

  return (
    <PageLayout
      title="Manage Permissions"
      subtitle="Configure granular page-level access control and operational capabilities for each user role in the system."
      actions={
        <div className="flex flex-col gap-1 min-w-[220px]">
          <span className="text-[10px] font-bold text-ink-400 uppercase tracking-wider">
            Select User Role
          </span>
          <Select
            value={selectedRoleId || ""}
            onChange={(e) => setSelectedRoleId(e.target.value)}
            options={[{ value: "", label: "Select Role" }, ...roles]}
            className="w-full text-xs font-semibold"
          />
        </div>
      }
      toolbar={
        selectedRoleId ? (
          <>
            {/* Search Bar */}
            <div className="w-64">
              <SearchInput
                placeholder="Search pages or modules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleDiscard}
                disabled={!isDirty}
              >
                Reset
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={savePermissions}
                disabled={!isDirty}
              >
                Save Changes
              </Button>
            </div>
          </>
        ) : null
      }
    >
      <ErrorMessage message={errorMsg} />

      {/* Permissions Grid Categories */}
      {!selectedRoleId ? (
        <div className="py-16 flex justify-center items-center">
          <EmptyState
            variant="search"
            icon={
              <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.75">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            }
            title="No Role Selected"
            description="To configure access controls, please select a user role from the dropdown menu above."
            size="md"
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {filteredPermissions.length > 0 ? (
            <Table className="flex-1 min-h-0 overflow-y-auto">
              <Table.Head>
                <tr>
                  <Table.HeadCell className="w-[40%] min-w-[200px] text-xs font-bold py-3">PAGE NAME</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-[12%] min-w-[80px] text-xs font-bold py-3">VIEW</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-[12%] min-w-[80px] text-xs font-bold py-3">ADD</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-[12%] min-w-[80px] text-xs font-bold py-3">EDIT</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-[12%] min-w-[80px] text-xs font-bold py-3">DELETE</Table.HeadCell>
                  <Table.HeadCell align="center" className="w-[12%] min-w-[80px] text-xs font-bold py-3">ALL</Table.HeadCell>
                </tr>
              </Table.Head>
              <Table.Body>
                {filteredPermissions.map((perm) => {
                  const meta = pageMeta[perm.pageName] || {
                    title: perm.pageName,
                    desc: "System page access",
                  };

                  return (
                    <Table.Row key={perm.pageId} className="hover:bg-canvas-dark/5 border-b border-border-light last:border-b-0">
                      {/* Page Name with Tree Connector */}
                      <Table.Cell className="!py-2">
                        <div className="flex items-center min-w-0 relative">
                          {perm.parentPageName ? (
                            <div className="relative w-8 h-10 flex-shrink-0 -my-3">
                              {/* Vertical line spacer */}
                              <div className="absolute left-4 top-0 bottom-0 w-px bg-border-medium"></div>
                              {/* Horizontal branch line */}
                              <div className="absolute left-4 top-1/2 w-4 h-px bg-border-medium"></div>
                            </div>
                          ) : null}
                          <div className="flex flex-col min-w-0 pl-2">
                            <span className={perm.parentPageName ? "text-sm font-semibold text-ink-900" : "text-sm font-bold text-primary-500"}>
                              {meta.title}
                            </span>
                            <span className="text-[11px] text-ink-500 leading-tight">
                              {meta.desc}
                            </span>
                          </div>
                        </div>
                      </Table.Cell>

                      {/* Checkboxes */}
                      {["view", "add", "edit", "del"].map((field) => {
                        const isView = field === "view";
                        const isDisabled = !isView && !perm.view;
                        return (
                          <Table.Cell key={field} align="center" className="!py-2">
                            <input
                              type="checkbox"
                              disabled={isDisabled}
                              className="form-checkbox rounded border-border text-primary-500 focus:ring-primary-500 h-4.5 w-4.5 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30 transition duration-150 ease-in-out"
                              checked={perm[field]}
                              onChange={handlePermissionChange(perm.pageId, field)}
                            />
                          </Table.Cell>
                        );
                      })}

                      {/* Toggle All */}
                      <Table.Cell align="center" className="!py-2">
                        <input
                          type="checkbox"
                          className="form-checkbox rounded border-border text-primary-500 focus:ring-primary-500 h-4.5 w-4.5 cursor-pointer transition duration-150 ease-in-out"
                          checked={perm.view && perm.add && perm.edit && perm.del}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setPermissions((prevPermissions) =>
                              prevPermissions.map((p) => {
                                if (p.pageId === perm.pageId) {
                                  return {
                                    ...p,
                                    view: isChecked,
                                    add: isChecked,
                                    edit: isChecked,
                                    del: isChecked,
                                  };
                                }
                                if (isChecked) {
                                  if (p.pageName === perm.parentPageName) {
                                    return {
                                      ...p,
                                      view: true,
                                      add: true,
                                      edit: true,
                                      del: true,
                                    };
                                  }
                                } else {
                                  if (p.parentPageName === perm.pageName) {
                                    return {
                                      ...p,
                                      view: false,
                                      add: false,
                                      edit: false,
                                      del: false,
                                    };
                                  }
                                }
                                return p;
                              })
                            );
                          }}
                        />
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          ) : (
            <div className="py-16 flex justify-center items-center">
              <EmptyState
                variant="search"
                title="No Matches Found"
                description="Try adjusting your search keywords."
                size="sm"
              />
            </div>
          )}
        </div>
      )}
    </PageLayout>
  );
};

export default Permissions;
